import { useMutation } from "@tanstack/react-query";
import { useCore } from "hooks/useCore";
import React, { useState } from "react";
import { createHealthFacility } from "service/hf";
import { toast } from "sonner";
import { clearAll } from "utils";

interface IFormData {
  home_service_fee: string;
  visible: boolean;
  name_of_facility: string;
  address: string;
  email: string;
  phone: string;
  opening_hour: string;
  closing_hour: string;
  logo: string;
  is_active: boolean;
  location: number;
  lga: number;
}

interface IResponse {
  message: string;
  health_facility: {
    home_service_fee: null;
    visible: false;
    name_of_facility: string;
    address: string;
    latitude: null;
    longitude: null;
    email: string;
    phone: string;
    opening_hour: string;
    closing_hour: string;
    logo: string;
    is_active: true;
    location: 10;
    lga: 10;
  };
}

export function useCreateHf() {
  const { allStateData } = useCore();

  const [formData, setFormData] = useState<IFormData>({
    home_service_fee: "",
    visible: false,
    name_of_facility: "",
    address: "",
    email: "",
    phone: "",
    opening_hour: "",
    closing_hour: "",
    logo: "",
    is_active: true,
    location: 0,
    lga: 0,
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleFileUpload = (key: keyof IFormData, file: File | null) => {
    setFormData((prevState) => ({
      ...prevState,
      [key]: file,
    }));
  };

  const allStateOption = allStateData?.results.map((state) => ({
    label: state.name,
    value: state.pk,
  }));

  const selectedState = allStateData?.results.find(
    (state) => state.pk === formData.location
  );

  const allLgaOption =
    selectedState?.lgas.map((lga) => ({
      label: lga.name,
      value: lga.pk,
    })) || [];

  const handleFieldChange = (
    field: keyof IFormData,
    value: string | number | boolean
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
      ...(field === "location" && { lga: 0 }),
    }));
  };

  const handleCreate = () => {
    mutate();
  };

  const isFormComplete = () => {
    return (
      formData.name_of_facility &&
      formData.address &&
      formData.email &&
      formData.phone &&
      formData.opening_hour &&
      formData.closing_hour &&
      formData.logo &&
      formData.location &&
      formData.lga
    );
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const formattedHour = hours % 12 || 12;
    return `${formattedHour} ${period}`;
  };

  const { mutate, isPending } = useMutation<IResponse>({
    mutationFn: async () => {
      const formBody = new FormData();

      formBody.append("name_of_facility", formData.name_of_facility);
      formBody.append("address", formData.address);
      formBody.append("email", formData.email);
      formBody.append("phone", `+${formData.phone}`);
      formBody.append("opening_hour", formatTime(formData.opening_hour));
      formBody.append("closing_hour", formatTime(formData.closing_hour));
      formBody.append("logo", formData.logo);
      formBody.append("location", formData.location.toString());
      formBody.append("lga", formData.lga.toString());
      formBody.append("is_active", formData.is_active.toString());
      formBody.append("visible", formData.visible.toString());
      formBody.append("home_service_fee", formData.home_service_fee);
      formBody.append("visible", formData.visible.toString());

      const response = await createHealthFacility(formBody);

      return response;
    },

    onSuccess: (data) => {
      toast.success(
        "Health Facility created successfully, You will be redirected to the login page, so changes can have effect"
      );

      setTimeout(() => {
        clearAll();
      }, 3000);
    },
    onError: (error: any) => {
      console.log(error);
    },
  });

  return {
    formData,
    isPending,
    allStateOption,
    allLgaOption,
    handleInputChange,
    handleFileUpload,
    handleCreate,
    handleFieldChange,
    isFormComplete,
  };
}
